// Client wrapper for the team-invite endpoints on the api service (served
// same-origin at /api/team/*). Mirrors lib/billingApi.ts: thin fetch helpers that
// forward the PocketBase session token so the server can resolve + authorize the
// caller. The invite/resend/role/revoke calls are owner/admin-only (the sidecar
// re-checks); the resulting seat changes stream back over the store's realtime
// team subscription, so these don't optimistically write the roster themselves.
//
// The two public calls — inviteInfo + acceptInvite — are used by the unauthenticated
// /accept-invite page and carry no token.

import type { RecordModel } from 'pocketbase'
import type { SeatRole } from './types'
import { onAuthExpired } from './pb'

const BASE = '/api/team'

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  opts: { body?: unknown; pbToken?: string; query?: Record<string, string> } = {},
): Promise<T> {
  const qs = opts.query ? `?${new URLSearchParams(opts.query)}` : ''
  const res = await fetch(`${BASE}${path}${qs}`, {
    method,
    headers: {
      ...(method === 'POST' ? { 'Content-Type': 'application/json' } : {}),
      ...(opts.pbToken ? { 'X-PB-Token': opts.pbToken } : {}),
    },
    body: method === 'POST' ? JSON.stringify(opts.body ?? {}) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  if (res.status === 401) onAuthExpired()
  if (!res.ok) throw new Error((data as { error?: string })?.error || 'Team request failed.')
  return data as T
}

/** Owner/admin: create a seat and email an invite link. `id` is a client-minted
 *  PocketBase id so the realtime create echo dedups against the optimistic row. */
export function inviteMember(
  input: { id: string; name: string; email: string; role: SeatRole },
  pbToken: string,
): Promise<{ ok: true; id: string; emailDelivered: boolean }> {
  return request('POST', '/invite', { body: input, pbToken })
}

/** Owner/admin: re-mint the token and email the invite again. */
export function resendInvite(id: string, pbToken: string): Promise<{ ok: true }> {
  return request('POST', '/resend', { body: { id }, pbToken })
}

/** Owner/admin: change a seat's role (and its linked login's seatRole). */
export function changeRole(id: string, role: SeatRole, pbToken: string): Promise<{ ok: true }> {
  return request('POST', '/role', { body: { id, role }, pbToken })
}

/** Owner/admin: remove a seat and disable its login. */
export function revokeMember(id: string, pbToken: string): Promise<{ ok: true }> {
  return request('POST', '/revoke', { body: { id }, pbToken })
}

export interface InviteInfo {
  name: string
  email: string
  role: SeatRole
  company: string
}

/** Public: look up a pending invite by its token (for the accept page). */
export function inviteInfo(token: string): Promise<InviteInfo> {
  return request('GET', '/invite', { query: { token } })
}

/** Propagate the signed-in recruiter's profile (name/initials/avatar) to their
 *  own team seat, so the roster matches the profile. No-op if they have no seat. */
export function syncProfile(pbToken: string): Promise<{ ok: true; synced: boolean }> {
  return request('POST', '/sync-profile', { pbToken })
}

/** Public: set a password → mint the recruiter login; returns a session to adopt. */
export function acceptInvite(input: {
  token: string
  name: string
  password: string
}): Promise<{ token: string; record: RecordModel }> {
  return request('POST', '/accept', { body: input })
}
